// Theme utility functions for applying custom themes

import type { CustomTheme } from './presets';
import {
  fontSizeMap,
  densityMap,
  borderRadiusMap,
  animationSpeedMap,
} from './presets';

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

// Generate lighter/darker shades from a hex color using HSL manipulation
function hexToHsl(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return [h * 360, s * 100, l * 100];
}

function hslToHex(h: number, s: number, l: number): string {
  h = h / 360;
  s = s / 100;
  l = l / 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (c: number) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function generatePrimaryShades(primary: string): Record<string, string> {
  const [h, s] = hexToHsl(primary);
  return {
    '--color-primary-50': hslToHex(h, Math.min(s, 100), 97),
    '--color-primary-100': hslToHex(h, Math.min(s, 100), 93),
    '--color-primary-200': hslToHex(h, Math.min(s, 100), 86),
    '--color-primary-300': hslToHex(h, Math.min(s, 100), 75),
    '--color-primary-400': hslToHex(h, Math.min(s, 100), 62),
    '--color-primary-700': hslToHex(h, Math.min(s + 10, 100), 38),
    '--color-primary-800': hslToHex(h, Math.min(s + 10, 100), 31),
    '--color-primary-900': hslToHex(h, Math.min(s + 10, 100), 24),
    '--color-primary-950': hslToHex(h, Math.min(s + 10, 100), 17),
  };
}

export function applyTheme(theme: CustomTheme): void {
  const root = document.documentElement;
  const effectiveMode = theme.mode === 'system' ? getSystemTheme() : theme.mode;

  // Apply dark mode class
  if (effectiveMode === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }

  // Apply color CSS variables
  root.style.setProperty('--color-primary', theme.colors.primary);
  root.style.setProperty('--color-primary-dark', theme.colors.primaryDark);
  root.style.setProperty('--color-accent', theme.colors.accent);
  root.style.setProperty('--color-accent-dark', theme.colors.accentDark);
  root.style.setProperty('--color-sidebar-light', theme.colors.sidebar);
  root.style.setProperty('--color-sidebar-dark', theme.colors.sidebarDark);

  // Generate and apply primary color shade palette
  const shades = generatePrimaryShades(theme.colors.primary);
  for (const [varName, colorValue] of Object.entries(shades)) {
    root.style.setProperty(varName, colorValue);
  }

  // Apply font size
  const fontSize = fontSizeMap[theme.fontSize];
  root.style.setProperty('--font-size-base', fontSize);
  root.style.fontSize = fontSize;

  // Apply density (affects spacing throughout the app)
  const densityMultiplier = densityMap[theme.density];
  root.style.setProperty('--density-multiplier', densityMultiplier.toString());

  // Spacing scale based on density
  const spacingBase = 0.25; // 4px base unit
  root.style.setProperty('--spacing-xs', `${spacingBase * 1 * densityMultiplier}rem`);
  root.style.setProperty('--spacing-sm', `${spacingBase * 2 * densityMultiplier}rem`);
  root.style.setProperty('--spacing-md', `${spacingBase * 3 * densityMultiplier}rem`);
  root.style.setProperty('--spacing-lg', `${spacingBase * 4 * densityMultiplier}rem`);
  root.style.setProperty('--spacing-xl', `${spacingBase * 6 * densityMultiplier}rem`);

  // Apply border radius
  const borderRadius = borderRadiusMap[theme.borderRadius];
  root.style.setProperty('--border-radius-sm', borderRadius);
  root.style.setProperty('--border-radius-md',
    theme.borderRadius === 'sharp' ? '0px' :
    theme.borderRadius === 'slightly-rounded' ? '0.375rem' :
    theme.borderRadius === 'rounded' ? '0.75rem' : '1.5rem'
  );
  root.style.setProperty('--border-radius-lg',
    theme.borderRadius === 'sharp' ? '0px' :
    theme.borderRadius === 'slightly-rounded' ? '0.5rem' :
    theme.borderRadius === 'rounded' ? '1rem' : '2rem'
  );

  // Apply animation speed
  const animationMultiplier = animationSpeedMap[theme.animationSpeed];

  if (animationMultiplier === 0) {
    // Disable all animations
    root.style.setProperty('--transition-fast', '0s');
    root.style.setProperty('--transition-normal', '0s');
    root.style.setProperty('--transition-slow', '0s');
    root.classList.add('reduce-motion');
  } else {
    root.classList.remove('reduce-motion');
    root.style.setProperty('--transition-fast', `${150 * animationMultiplier}ms`);
    root.style.setProperty('--transition-normal', `${300 * animationMultiplier}ms`);
    root.style.setProperty('--transition-slow', `${500 * animationMultiplier}ms`);
  }

  // Store theme ID for reference
  root.setAttribute('data-theme', theme.id);
}

// Sync theme changes across browser tabs
export function setupThemeSync(onThemeChange: (theme: CustomTheme) => void): () => void {
  const handleStorageChange = (e: StorageEvent) => {
    if (e.key === 'custom-theme-storage' && e.newValue) {
      try {
        const data = JSON.parse(e.newValue);
        if (data.state?.customTheme) {
          onThemeChange(data.state.customTheme);
        }
      } catch (error) {
        console.error('Failed to sync theme from storage:', error);
      }
    }
  };

  window.addEventListener('storage', handleStorageChange);

  return () => {
    window.removeEventListener('storage', handleStorageChange);
  };
}

// Listen for system theme preference changes
export function setupSystemThemeListener(onSystemThemeChange: () => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return () => {};
  }

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

  const handleChange = () => {
    onSystemThemeChange();
  };

  mediaQuery.addEventListener('change', handleChange);

  return () => {
    mediaQuery.removeEventListener('change', handleChange);
  };
}
